import { take, map, max, path, reduce, min } from 'ramda';
import React from 'react';
import { compose } from 'recompose';
import styled from 'styled-components';
import { white } from '@peloton/colors';
import { deepUpdateOnKeys } from '@peloton/hocs';
import { scrollToTop } from '@peloton/navigation';
import { DifficultyLevel } from '@engage/classes';
import { slate1, gray4, superRedA11y } from '@engage/colors';
import type {
  InstructorFilterValue,
  FilterValue,
  Selectable,
  toggle,
  SelectableCollectionFilterGroup,
  SelectableToggleFilterGroup,
  Sort,
  SortValue,
} from '@engage/filters';
import { FilterName } from '@engage/filters';
import { spaces } from '@engage/styles';
import { label14Caps, label14, label12Caps, number32 } from '@engage/typography';
import type { Option } from '@members/accordions';
import { SelectableAccordion } from '@members/accordions';
import { Classes, useClasses } from '@members/copy';
import { SimpleAvatar } from '@members/images';
import { Indicator } from '@members/labels/DifficultyLabel';
import BodyActivity from '../icons/BodyActivity';
import Bookmark from '../icons/Bookmark';
import ClassLanguage from '../icons/ClassLanguage';
import ClassTypes from '../icons/ClassTypes';
import Difficulty from '../icons/Difficulty';
import { Free } from '../icons/Free';
import Instructors from '../icons/Instructors';
import Length from '../icons/Length';
import Music from '../icons/Music';
import SortIcon from '../icons/SortIcon';
import { Special } from '../icons/Special';
import Subtitles from '../icons/Subtitles';

const MAX_3_COL_NAME = 6;
const MAX_2_COL_NAME = 15;

const CellContainerMultiSelect = styled.div`
  ${label14}
  align-items: center;
  display: flex;
  padding: 14px 10px;
  flex-direction: column;
`;
const CellContainerDuration = styled(CellContainerMultiSelect)`
  padding: 18px 0px;
`;

const Label = styled.span`
  text-overflow: ellipsis;
  overflow: hidden;
`;
const LabelMultiSelect = styled(Label)`
  padding-top: ${spaces.medium}px;
`;

const CenteredTopLabel = styled.h1`
  margin: 0 auto;
  text-align: center;
  padding-bottom: ${spaces.medium}px;
  ${number32}
`;
const CenteredLabel = styled(Label)`
  margin: 0 auto;
  text-align: center;
`;
const Wrapper = styled.div`
  background: ${white};
  border-radius: 5px;
  width: 100%;
`;

const StyledIndicator = styled(Indicator)`
  width: 8px;
  height: 8px;
  margin-right: ${spaces.xxSmall}px;
`;

const DifficultyLabel = styled(Label)`
  ${label14Caps}
  line-height: 19px;
`;

const CellContainerMultiSelectDifficulty = styled.div`
  ${label12Caps}
  align-items: center;
  display: flex;
  padding: 14px 10px;
  flex-direction: row;
  justify-content: center;
`;

type AccordionCellProps = {
  displayName: string;
  displayImageUrl?: string;
  filterGroupName?: string;
  value?: string;
  displayAllClasses?: boolean;
  localeName?: string;
};

const CellWithImageMultiSelect = ({
  displayImageUrl,
  displayName,
}: AccordionCellProps) => (
  <CellContainerMultiSelect data-test-id="filtersCellMultiSelect">
    <SimpleAvatar size={48} url={displayImageUrl} alt="" />
    <LabelMultiSelect>{displayName.split(' ')[0]}</LabelMultiSelect>
  </CellContainerMultiSelect>
);

const CellMultiSelect = ({ displayName }: { displayName: string }) => (
  <CellContainerMultiSelect data-test-id="filtersCellMultiSelect">
    <CenteredLabel>{displayName}</CenteredLabel>
  </CellContainerMultiSelect>
);

const CellMultiSelectDuration = ({ displayName }: { displayName: string }) => (
  <CellContainerDuration data-test-id="filtersCellDuration">
    <CenteredTopLabel>{displayName.split(' ')[0]}</CenteredTopLabel>
    <CenteredLabel>{displayName.split(' ')[1]}</CenteredLabel>
  </CellContainerDuration>
);

const CellMultiSelectDifficulty = ({
  displayName,
  value,
}: {
  displayName: string;
  value?: string;
}) => (
  <CellContainerMultiSelectDifficulty data-test-id="filtersCellDifficulty">
    <StyledIndicator level={toDifficultyLevel(value ?? '')} />
    <DifficultyLabel>{displayName.toUpperCase()}</DifficultyLabel>
  </CellContainerMultiSelectDifficulty>
);

export const AccordionCell = (props: AccordionCellProps) => {
  if (props.displayImageUrl) {
    return <CellWithImageMultiSelect {...props} />;
  }
  if (props.filterGroupName === 'duration') {
    return <CellMultiSelectDuration {...props} />;
  }
  if (props.filterGroupName === 'difficulty_level') {
    return <CellMultiSelectDifficulty {...props} />;
  }
  return <CellMultiSelect {...props} />;
};

/* eslint-disable react/display-name */
const toAccordionOption = (filterGroupName: string) => ({
  selected,
  group,
  ...filter
}: Selectable<FilterValue | InstructorFilterValue>): Option => ({
  content: <AccordionCell filterGroupName={filterGroupName} {...filter} />,
  label: filter.displayName,
  value: filter.value,
  group,
  selected,
});

const getIcon = (name: FilterName) => {
  switch (name) {
    case FilterName.Duration: {
      return <Length />;
    }
    case FilterName.InstructorId: {
      return <Instructors />;
    }
    case FilterName.Subtitles: {
      return <Subtitles />;
    }
    case FilterName.ClassTypeId: {
      return <ClassTypes />;
    }
    case FilterName.MusicGenre: {
      return <Music />;
    }
    case FilterName.IsFavoriteRide: {
      return <Bookmark />;
    }
    case FilterName.IsFreemium: {
      return <Free />;
    }
    case FilterName.Special: {
      return <Special />;
    }
    case FilterName.DifficultyLevel: {
      return <Difficulty />;
    }
    case FilterName.BodyActivity: {
      return <BodyActivity />;
    }
    case FilterName.ClassLanguages: {
      return <ClassLanguage />;
    }
    default:
      return undefined;
  }
};

const sortOptions = (unsortedOptions: { [key: string]: Option[] }) => {
  return Object.keys(unsortedOptions)
    .sort()
    .reduce(function (acc, key) {
      acc[key] = unsortedOptions[key];
      return acc;
    }, {});
};

const createGroupedOptions = (options: Option[]) => {
  const groupedNonSortedOptions: { [key: string]: Option[] } = {};
  options.forEach((option: any) => {
    if (option.group) {
      if (groupedNonSortedOptions[option.group!]) {
        groupedNonSortedOptions[option.group].push(option);
      } else {
        groupedNonSortedOptions[option.group!] = [option];
      }
    }
  });

  return sortOptions(groupedNonSortedOptions);
};

const CollectionFilter: React.FC<React.PropsWithChildren<Props>> = ({
  filterGroup,
  toggleFilter,
  grouped,
}) => {
  const options = take(1000, filterGroup.values).map(toAccordionOption(filterGroup.name));

  return (
    <FilterGroupListItem>
      <SelectableAccordion
        title={filterGroup.displayName}
        options={options}
        groupedOptions={grouped ? createGroupedOptions(options) : undefined}
        onToggle={(val: string) => {
          toggleFilter(filterGroup.name, val, undefined);
          scrollToTop();
        }}
        columns={
          filterGroup.columns
            ? filterGroup.columns
            : toNumberOfColumns(filterGroup.values)
        }
        icon={getIcon(filterGroup.name)}
        name={filterGroup.name}
      />
    </FilterGroupListItem>
  );
};

const IconContainer = styled.div`
  align-items: center;
  justify-content: center;
  display: flex;
`;

const ListTitle = styled.span`
  ${label14Caps}
  padding: ${spaces.xxxSmall}px ${spaces.xSmall}px;
`;

const CellWrapper = styled.div`
  display: flex;
  width: 100%;
  flex: 1;
  justify-content: space-between;
  align-items: center;
  padding: 0 15px;
  height: 50px;
  color: ${slate1};
`;

const FilterToggleWrapper = styled.div`
  position: relative;
`;

const FilterToggleLabel = styled.label`
  position: absolute;
  top: 0;
  left: 0;
  width: 60px;
  height: 30px;
  border-radius: 15px;
  background: ${gray4};
  cursor: pointer;
  &::after {
    content: '';
    display: block;
    border-radius: 50%;
    width: 26px;
    height: 26px;
    margin: ${spaces.tiny}px;
    background: #ffffff;
    transition: 0.3s;
  }
`;

const FilterToggle = styled.input`
  opacity: 0;
  z-index: 1;
  border-radius: 15px;
  width: 60px;
  height: 30px;
  &:checked + ${FilterToggleLabel} {
    background: ${superRedA11y};
    &::after {
      content: '';
      display: block;
      border-radius: 50%;
      width: 26px;
      height: 26px;
      margin-left: ${spaces.xxxLarge}px;
      transition: 0.3s;
    }
  }
`;

export const isSelected = (
  group: SelectableToggleFilterGroup | SelectableCollectionFilterGroup,
  order: number,
): boolean => Boolean(group.values.length && group.values[order].selected);

export const ToggleFilter: React.FC<React.PropsWithChildren<Props>> = ({
  filterGroup,
  toggleFilter,
}) => {
  const ID = `${filterGroup.displayName}Checkbox`;

  const triggerToggleClick = () => {
    toggleFilter(filterGroup.name, 'true');
    scrollToTop();
  };
  return (
    <FilterGroupListItem>
      <Wrapper>
        <CellWrapper>
          <IconContainer
            data-test-id={`${filterGroup.displayName.split(' ').join('')}Filter`}
          >
            {getIcon(filterGroup.name)} <ListTitle>{filterGroup.displayName}</ListTitle>
          </IconContainer>
          <FilterToggleWrapper
            data-test-id={`${filterGroup.displayName?.split(' ').join('')}Toggle`}
          >
            <FilterToggle
              id={ID}
              type="checkbox"
              checked={isSelected(filterGroup, 0)}
              onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                triggerToggleClick()
              }
            />
            {/* eslint-disable-next-line styled-components-a11y/label-has-associated-control */}
            <FilterToggleLabel htmlFor={ID} />
          </FilterToggleWrapper>
        </CellWrapper>
      </Wrapper>
    </FilterGroupListItem>
  );
};

const toLongestDisplayName = (values: FilterValue[] | Sort[]): number =>
  reduce<FilterValue | Sort | undefined, number>(
    max,
    0,
    map(path(['displayName', 'length']), values),
  );

const toNumberOfColumns = (values: FilterValue[] | Sort[]) => {
  const longestDisplayName = toLongestDisplayName(values);
  let columns = 3;
  if (longestDisplayName > MAX_2_COL_NAME) {
    columns = 1;
  } else if (longestDisplayName > MAX_3_COL_NAME) {
    columns = 2;
  }
  return min(columns, values.length);
};

const toDifficultyLevel = (value: string | undefined) => {
  switch (value) {
    case 'advanced':
      return DifficultyLevel.Advanced;
    case 'intermediate':
      return DifficultyLevel.Intermediate;
    case 'beginner':
    default:
      return DifficultyLevel.Beginner;
  }
};

const FilterGroupListItem = styled.li`
  width: 100%;
  padding-bottom: 10px;
`;

type Props = {
  filterGroup: SelectableCollectionFilterGroup | SelectableToggleFilterGroup;
  toggleFilter: typeof toggle;
  grouped?: boolean;
};

type SortProps = {
  sorts: Selectable<Sort>[];
  selectSort(value: SortValue): void;
};

const toSortOption = ({ displayName, value, selected }: Selectable<Sort>) => ({
  content: <AccordionCell displayName={displayName} />,
  label: displayName,
  value,
  selected,
});

const SortGroup: React.FC<React.PropsWithChildren<SortProps>> = ({
  sorts = [],
  selectSort,
}) => (
  <FilterGroupListItem>
    <SelectableAccordion
      title={useClasses(Classes.FilterSort)}
      options={sorts.map(toSortOption)}
      onToggle={(value: SortValue) => {
        selectSort(value);
        scrollToTop();
      }}
      columns={toNumberOfColumns(sorts)}
      icon={<SortIcon />}
      name={useClasses(Classes.FilterSort)}
    />
  </FilterGroupListItem>
);

export { SortGroup };
export default compose<{}, Props>(
  deepUpdateOnKeys<Props>(['filterGroup']),
)(CollectionFilter);
